/**
 * Player state machine — 04-combat §1–§3, 05-input §1.
 * One function per tick per player; everything reads `p.tuning`.
 */
import { dirToWorld } from './arena.js'
import { clamp } from './vec.js'
import type { AppliedInput, Bounds, PlayerState, PlayerStateName, SimState, Vec2 } from './types.js'
import { BOUNDS_PUSH_SPEED, DISC_FLIGHT_Y, JUMP_COST, JUMP_LAND_TICKS, JUMP_V0 } from './constants.js'
import { applyGravity } from './gravity.js'

/** What the current phase permits (04 §8). */
export interface Permissions {
  move: boolean
  dash: boolean
  throw: boolean
  shield: boolean
  /** Follows `dash` (spec §1.2): countdown yes, RESULTS/pause no. */
  jump: boolean
}
export const ALL_ALLOWED: Permissions = { move: true, dash: true, throw: true, shield: true, jump: true }

export function setState(p: PlayerState, st: PlayerStateName): void {
  p.state = st
  p.stateTicks = 0
}

/** Can this player receive a RETURNING disc this tick (03 §5)? */
export function canCatch(p: PlayerState): boolean {
  return p.alive && p.discsHeld.length < p.maxDiscs
    && p.state !== 'DASHING' && p.state !== 'SHIELD' && p.state !== 'THROW_WINDUP' && p.state !== 'EXHAUST'
}
/** Can this player pick a GROUND disc up (03 §6)? Feet on the floor (spec §1.3.5). */
export function canPickup(p: PlayerState): boolean {
  return p.alive && p.grounded && p.discsHeld.length < p.maxDiscs && p.state !== 'DASHING'
}

/** Jump-dodge take-off (spec §1.3.4): on the floor, out of the landing lock, in a state whose
 *  body can hop (EXHAUST included, like throw; SHIELD/DASHING/WINDUP cannot), stamina ≥ JUMP_COST. */
export function canJump(p: PlayerState): boolean {
  return p.grounded && p.landTicks === 0
    && (p.state === 'IDLE' || p.state === 'THROW_RECOVERY' || p.state === 'EXHAUST')
    && p.stamina >= JUMP_COST
}

function canDash(p: PlayerState): boolean {
  return p.grounded && p.landTicks === 0
    && (p.state === 'IDLE' || p.state === 'THROW_RECOVERY') && p.dashCooldownTicks === 0 && p.stamina >= p.tuning.DASH_COST
}
function canThrow(p: PlayerState): boolean {
  // 04 §1: throwing is allowed from IDLE and from EXHAUST ("can still throw/catch"); in the air too (spec §1.3.5), not in the landing lock.
  return p.landTicks === 0 && (p.state === 'IDLE' || p.state === 'EXHAUST') && p.discsHeld.length > 0
}
function canRaiseShield(p: PlayerState): boolean {
  if (!p.grounded || p.landTicks > 0) return false // spec §1.3.5 / A04-J5: no shield in the air or in the landing lock
  if (p.shieldLockoutTicks > 0 || p.stamina < p.tuning.SHIELD_MIN_RAISE) return false
  if (p.state === 'IDLE') return true
  if (p.state === 'THROW_RECOVERY') return true // lockout counter already enforces THROW_SHIELD_LOCK
  return false
}

export function stepPlayer(s: SimState, p: PlayerState, input: AppliedInput | undefined, dt: number, perm: Permissions): void {
  const t = p.tuning
  if (input) p.lastInputSeq = Math.max(p.lastInputSeq, input.seq)

  // Counters.
  p.stateTicks += 1
  if (p.regenDelayTicks > 0) p.regenDelayTicks -= 1
  if (p.dashCooldownTicks > 0) p.dashCooldownTicks -= 1
  if (p.shieldLockoutTicks > 0) p.shieldLockoutTicks -= 1
  if (p.landTicks > 0) p.landTicks -= 1
  if (p.state === 'SHIELD') p.shieldAgeTicks += 1

  if (!p.alive) { p.vel.x = p.vel.z = 0; return }

  // Edge detection (05 §1: THROW/DASH edge-latched, SHIELD level).
  const throwEdge = !!input?.throw && !p.prevButtons.throw
  const dashEdge = !!input?.dash && !p.prevButtons.dash
  const jumpEdge = !!input?.jump && !p.prevButtons.jump
  const shieldHeld = !!input?.shield
  if (input) p.prevButtons = { throw: input.throw, dash: input.dash, jump: !!input.jump }

  // Aim tracks input every tick (the shield arc turns with it — 04 §3).
  if (input) p.aim = { x: Math.cos(input.aim), z: Math.sin(input.aim) }

  // Move vector: screen-space → world via the team transform (02 §1), clamped.
  let move: Vec2 = { x: 0, z: 0 }
  if (input && perm.move) {
    const m = dirToWorld({ x: clamp(input.mx, -1, 1), z: clamp(input.my, -1, 1) }, p.team)
    const l = Math.hypot(m.x, m.z)
    move = l > 1 ? { x: m.x / l, z: m.z / l } : m
  }
  const hasMove = Math.hypot(move.x, move.z) > 1e-6

  // ── Timed state exits ───────────────────────────────────────────────────
  if (p.state === 'THROW_WINDUP' && p.stateTicks >= t.THROW_WINDUP_TICKS) {
    releaseThrow(s, p)
  } else if (p.state === 'THROW_RECOVERY' && p.stateTicks >= t.THROW_RECOVERY_TICKS) {
    setState(p, 'IDLE')
  } else if (p.state === 'DASHING' && p.stateTicks >= t.DASH_TICKS) {
    // No dead stop: snap to dir × MOVE_SPEED (04 §2).
    p.vel.x = p.dashDir.x * t.MOVE_SPEED
    p.vel.z = p.dashDir.z * t.MOVE_SPEED
    p.dashCooldownTicks = t.DASH_COOLDOWN_TICKS
    setState(p, 'IDLE')
  } else if (p.state === 'EXHAUST' && p.stateTicks >= t.EXHAUST_TICKS) {
    setState(p, 'IDLE')
  }

  // ── Airborne integration + landing (spec §1.3.2), before actions so a touchdown's
  //    landing lock applies to this tick's inputs. Pad clamping still holds in the
  //    air (below), so VOID_KILL_Y is unreachable this sprint.
  if (!p.grounded) {
    applyGravity(p, dt)
    if (p.pos.y <= s.arena.floorY) {
      p.pos.y = s.arena.floorY
      p.vel.y = 0
      p.grounded = true
      p.landTicks = JUMP_LAND_TICKS
      s.events.push({ type: 'land', tick: s.tick, playerId: p.id })
    }
  }

  // ── Shield level (04 §3, 04 §12: re-evaluated every tick while held) ────
  if (p.state === 'SHIELD') {
    if (!shieldHeld || !perm.shield) {
      lowerShield(s, p)
    } else {
      p.stamina -= t.SHIELD_DRAIN * dt
      p.roundStats.shieldTicks += 1
      if (p.stamina <= 0) {
        p.stamina = 0
        exhaust(s, p)
      }
    }
  }

  // ── Actions, same-tick precedence: DASH > JUMP > THROW > SHIELD (04 §12, spec §1.3.4) ──
  if (dashEdge && perm.dash && canDash(p)) {
    const dir = hasMove ? move : { x: p.aim.x, z: p.aim.z }
    p.dashDir = { x: dir.x, z: dir.z }
    p.stamina -= t.DASH_COST
    p.regenDelayTicks = t.STAMINA_REGEN_DELAY_TICKS
    setState(p, 'DASHING')
    p.roundStats.dashes += 1
    s.events.push({ type: 'dash', tick: s.tick, playerId: p.id, dx: dir.x, dz: dir.z })
  } else if (jumpEdge && perm.jump && canJump(p)) {
    p.vel.y = JUMP_V0
    p.grounded = false
    p.stamina -= JUMP_COST
    p.regenDelayTicks = t.STAMINA_REGEN_DELAY_TICKS
    p.roundStats.jumps += 1
    s.events.push({ type: 'jump', tick: s.tick, playerId: p.id, vx: p.vel.x, vz: p.vel.z })
  } else if (throwEdge && perm.throw && canThrow(p)) {
    p.throwAim = { x: p.aim.x, z: p.aim.z } // latched (03 §3, 05 §3)
    setState(p, 'THROW_WINDUP')
  } else if (shieldHeld && perm.shield && p.state !== 'SHIELD' && canRaiseShield(p)) {
    p.stamina -= t.SHIELD_RAISE_COST
    p.shieldAgeTicks = 0
    setState(p, 'SHIELD')
    s.events.push({ type: 'shieldRaise', tick: s.tick, playerId: p.id })
  }

  // ── Stamina regen (no regen while SHIELD or during delay) ───────────────
  if (p.state !== 'SHIELD' && p.regenDelayTicks === 0) {
    p.stamina = Math.min(t.STAMINA_MAX, p.stamina + t.STAMINA_REGEN * dt)
  }

  // ── Movement (04 §2) ───────────────────────────────────────────────────
  if (p.state === 'DASHING') {
    p.vel.x = p.dashDir.x * t.DASH_SPEED
    p.vel.z = p.dashDir.z * t.DASH_SPEED
  } else {
    const cap = t.MOVE_SPEED * (p.state === 'SHIELD' ? t.SHIELD_MOVE_MULT : 1)
    const tx = move.x * cap
    const tz = move.z * cap
    const rate = (hasMove ? t.MOVE_ACCEL : t.MOVE_DECEL) * dt
    const dx = tx - p.vel.x
    const dz = tz - p.vel.z
    const dl = Math.hypot(dx, dz)
    if (dl <= rate) { p.vel.x = tx; p.vel.z = tz } else { p.vel.x += (dx / dl) * rate; p.vel.z += (dz / dl) * rate }
  }
  p.pos.x += p.vel.x * dt
  p.pos.z += p.vel.z * dt
  if (p.grounded) { p.vel.y = 0; p.pos.y = s.arena.floorY }
  clampToBounds(p, s.match.bounds[p.team], dt)

  // Held discs ride along.
  for (const id of p.discsHeld) {
    const d = s.discs.find((x) => x.id === id)
    if (d) { d.pos.x = p.pos.x; d.pos.z = p.pos.z; d.pos.y = DISC_FLIGHT_Y }
  }
}

/** Pad/bounds clamp (02 §5) with overtime push (04 §7): the rail is hard for
 *  motion *into* it (that velocity component is zeroed, tangent kept); a
 *  player left outside by a shrinking bound is pushed in at ≤ BOUNDS_PUSH_SPEED. */
export function clampToBounds(p: PlayerState, b: Bounds, dt: number): void {
  const r = p.tuning.PLAYER_RADIUS
  const maxPush = BOUNDS_PUSH_SPEED * dt
  const axis = (pos: number, vel: number, lo: number, hi: number): [number, number] => {
    if (pos < lo) { const d = lo - pos; return d <= maxPush + 1e-9 ? [lo, vel < 0 ? 0 : vel] : [pos + maxPush, vel < 0 ? 0 : vel] }
    if (pos > hi) { const d = pos - hi; return d <= maxPush + 1e-9 ? [hi, vel > 0 ? 0 : vel] : [pos - maxPush, vel > 0 ? 0 : vel] }
    return [pos, vel]
  }
  ;[p.pos.x, p.vel.x] = axis(p.pos.x, p.vel.x, b.xMin + r, b.xMax - r)
  ;[p.pos.z, p.vel.z] = axis(p.pos.z, p.vel.z, b.zMin + r, b.zMax - r)
}

export function lowerShield(s: SimState, p: PlayerState): void {
  setState(p, 'IDLE')
  p.shieldLockoutTicks = Math.max(p.shieldLockoutTicks, p.tuning.SHIELD_DROP_LOCKOUT_TICKS)
  p.regenDelayTicks = Math.max(p.regenDelayTicks, p.tuning.STAMINA_REGEN_DELAY_TICKS)
  s.events.push({ type: 'shieldLower', tick: s.tick, playerId: p.id })
}

export function exhaust(s: SimState, p: PlayerState): void {
  setState(p, 'EXHAUST')
  p.regenDelayTicks = Math.max(p.regenDelayTicks, p.tuning.STAMINA_REGEN_DELAY_TICKS)
  // The 1.0 s no-dash/no-shield lockout (04 §3) must survive a throw taken
  // from EXHAUST (which moves the state to THROW_WINDUP/RECOVERY), so it is
  // also carried by the counters, not only by the state.
  p.shieldLockoutTicks = Math.max(p.shieldLockoutTicks, p.tuning.EXHAUST_TICKS)
  p.dashCooldownTicks = Math.max(p.dashCooldownTicks, p.tuning.EXHAUST_TICKS)
  s.events.push({ type: 'exhaust', tick: s.tick, playerId: p.id })
}

/** Windup end → disc into FLIGHT (03 §3). */
function releaseThrow(s: SimState, p: PlayerState): void {
  const t = p.tuning
  const id = p.discsHeld.shift()
  const d = id === undefined ? undefined : s.discs.find((x) => x.id === id)
  setState(p, 'THROW_RECOVERY')
  p.shieldLockoutTicks = Math.max(p.shieldLockoutTicks, t.THROW_SHIELD_LOCK_TICKS)
  if (!d) return
  // Disc-scoped tuning is copied from the thrower at throw (06 §1).
  for (const k of Object.keys(d.tuning) as (keyof typeof d.tuning)[]) (d.tuning as Record<string, unknown>)[k] = t[k]
  const a = p.throwAim
  let x = p.pos.x + a.x * t.THROW_ORIGIN_OFFSET
  let z = p.pos.z + a.z * t.THROW_ORIGIN_OFFSET
  // Clamp inside walls/pillars (02 §8).
  const inset = d.tuning.DISC_RADIUS + 0.05
  const hw = s.arena.width / 2
  x = clamp(x, -hw + inset, hw - inset)
  z = clamp(z, inset, s.arena.length - inset)
  for (const pl of s.arena.pillars) {
    const dx = x - pl.x, dz = z - pl.z
    const l = Math.hypot(dx, dz)
    const min = pl.r + inset
    if (l < min) { const f = l < 1e-6 ? 1 : min / l; x = pl.x + (l < 1e-6 ? min : dx * f); z = pl.z + (l < 1e-6 ? 0 : dz * f) }
  }
  d.pos = { x, y: DISC_FLIGHT_Y, z }
  d.vel = { x: a.x * d.tuning.DISC_THROW_SPEED, y: 0, z: a.z * d.tuning.DISC_THROW_SPEED }
  d.rangeLeft = d.tuning.DISC_RANGE
  d.bounceCount = 0
  d.thrower = p.id
  d.team = p.team
  d.holder = null
  d.state = 'FLIGHT'
  d.stateTicks = 0
  d.lastZoneTeam = p.team
  p.roundStats.throws += 1
  if (p.invulnUntilTick > s.tick) { p.invulnUntilTick = 0; s.events.push({ type: 'invulnEnd', tick: s.tick, playerId: p.id }) }
  s.events.push({ type: 'throw', tick: s.tick, playerId: p.id, discId: d.id, x, z })
}
